import React, {
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import {
  colors,
  easingTokens,
  radius,
  s8,
  s16,
  shadowM,
  transitionTokens,
} from '@ardoq/design-tokens';
import { text1 } from '@ardoq/typography';
import { isMac } from '@ardoq/common-helpers';
import { t, type WithI18nLocale } from '@ardoq/i18n';

const HINT_LINGER_MS = 1200;
const ACTIVE_BORDER_WIDTH = 2;

const Container = styled.div<{ $isActive: boolean }>`
  position: relative;
  isolation: isolate;
  width: 100%;
  height: 100%;
  border-radius: inherit;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 1;
    pointer-events: none;
    border-radius: inherit;
    border: ${ACTIVE_BORDER_WIDTH}px solid
      ${props => (props.$isActive ? colors.borderBrandStrong : 'transparent')};
  }
`;

const HintLayer = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  inset: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  border-radius: inherit;

  background: ${props => (props.$isVisible ? colors.black100 : 'transparent')};

  opacity: ${props => (props.$isVisible ? 1 : 0)};
  transition: opacity ${transitionTokens.medium} ${easingTokens.cubicOut};
`;

const HintLabel = styled.div`
  ${text1};

  padding: ${s8} ${s16};
  border-radius: ${radius.medium};
  background: ${colors.surfaceInverted};
  color: ${colors.textOnInverted};
  box-shadow: ${shadowM};
`;

// A trackpad pinch is delivered as a wheel event with `ctrlKey` set and no key
// actually held, so ctrl always counts as a navigation gesture. macOS adds the
// ⌘ key on top, which is what the hint advertises there.
const hasNavigationModifier = (event: WheelEvent): boolean =>
  event.ctrlKey || (isMac() && event.metaKey);

const isNavigationModifierKey = (event: KeyboardEvent): boolean =>
  event.key === 'Control' || (isMac() && event.key === 'Meta');

const navigationModifierLabel = (): string => (isMac() ? '⌘' : 'Ctrl');

type HintVisibility = {
  isHintVisible: boolean;
  showHint: VoidFunction;
  hideHint: VoidFunction;
};

const useHintVisibility = (): HintVisibility => {
  const hideTimeoutRef = useRef<number | undefined>(undefined);
  const [isHintVisible, setIsHintVisible] = useState(false);

  const hideHint = useCallback(() => {
    window.clearTimeout(hideTimeoutRef.current);
    setIsHintVisible(false);
  }, []);

  const showHint = useCallback(() => {
    window.clearTimeout(hideTimeoutRef.current);
    setIsHintVisible(true);
    hideTimeoutRef.current = window.setTimeout(hideHint, HINT_LINGER_MS);
  }, [hideHint]);

  useEffect(() => () => window.clearTimeout(hideTimeoutRef.current), []);

  return { isHintVisible, showHint, hideHint };
};

/**
 * Clicking the graph hands it the wheel until the reader clicks away or presses
 * Escape, so a session of panning and zooming costs one modifier-free gesture.
 */
const useActivation = (
  containerRef: RefObject<HTMLDivElement | null>
): { isActive: boolean; activate: VoidFunction } => {
  const [isActive, setIsActive] = useState(false);

  const activate = useCallback(() => setIsActive(true), []);

  useEffect(() => {
    if (!isActive) return;

    const releaseOnOutsidePointerDown = (event: PointerEvent) => {
      if (containerRef.current?.contains(event.target as Node)) return;
      setIsActive(false);
    };
    // `@ardoq/modal` and `@ardoq/drawer` close on any Escape reaching their own
    // bubble-phase `document` listener, so releasing a graph inside one would
    // close it too. Claiming the key in capture phase keeps Escape meaning
    // "release the graph" for as long as the graph holds the wheel.
    const releaseOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      setIsActive(false);
    };

    document.addEventListener('pointerdown', releaseOnOutsidePointerDown);
    document.addEventListener('keydown', releaseOnEscape, { capture: true });
    return () => {
      document.removeEventListener('pointerdown', releaseOnOutsidePointerDown);
      document.removeEventListener('keydown', releaseOnEscape, {
        capture: true,
      });
    };
  }, [containerRef, isActive]);

  return { isActive, activate };
};

const useWheelGate = (
  containerRef: RefObject<HTMLDivElement | null>,
  isActive: boolean,
  showHint: VoidFunction,
  hideHint: VoidFunction
): void => {
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Capture-phase `stopPropagation` keeps the wheel event from ever reaching the
    // graph, while leaving the browser default intact so the page still scrolls.
    // Chrome's passive-by-default intervention covers only window/document/body,
    // so an element listener has to opt in or it blocks that scroll on the main
    // thread — which a yFiles graph keeps busy.
    const handleWheel = (event: WheelEvent) => {
      if (isActive || hasNavigationModifier(event)) {
        hideHint();
        return;
      }

      event.stopPropagation();
      showHint();
    };

    container.addEventListener('wheel', handleWheel, {
      capture: true,
      passive: true,
    });
    return () =>
      container.removeEventListener('wheel', handleWheel, { capture: true });
  }, [containerRef, isActive, showHint, hideHint]);
};

const useDismissHintOnModifierKey = (
  isHintVisible: boolean,
  hideHint: VoidFunction
): void => {
  useEffect(() => {
    if (!isHintVisible) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (isNavigationModifierKey(event)) hideHint();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isHintVisible, hideHint]);
};

export type EmbeddedGraphProps = WithI18nLocale;

/** Wrap a graph embedded in a scrollable page so the wheel scrolls the page until the reader clicks the graph or holds the platform modifier. */
export const EmbeddedGraph = (
  props: React.PropsWithChildren<EmbeddedGraphProps>
) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { isHintVisible, showHint, hideHint } = useHintVisibility();
  const { isActive, activate } = useActivation(containerRef);

  useWheelGate(containerRef, isActive, showHint, hideHint);
  useDismissHintOnModifierKey(isHintVisible, hideHint);

  return (
    <Container ref={containerRef} $isActive={isActive} onPointerDown={activate}>
      {props.children}
      <HintLayer $isVisible={isHintVisible} aria-hidden={!isHintVisible}>
        <HintLabel>
          {t(
            props.i18nLocale,
            'graphView:embeddedGraph.scrollHint',
            { modifier: navigationModifierLabel() },
            'Click the graph or hold {{modifier}} to navigate it'
          )}
        </HintLabel>
      </HintLayer>
    </Container>
  );
};
